// Column definitions for the Concepts table view.
// Rendered inside ConceptGroupsSection when the user toggles to "Table".
// Every row still surfaces the concept→group relationship via a dedicated
// Group column so the flat view carries the same context as the accordion.
import type { Column } from "../../../../components/DataTable";
import type { ConceptFinding } from "../../api/contentRegistryApi";
import { EMPTY_PLACEHOLDER } from "../../constants";
import { plainText } from "../../utils";
import {
  ConceptConfidenceBar,
  ConceptIdChip,
  SectionChip,
} from "../conceptIntelligenceParts";
import {
  CONCEPT_GROUPS_COPY,
  SOURCE_BADGE_DEFINED_CLS,
  SOURCE_BADGE_REFERENCED_CLS,
} from "./constants";
import { isDefinedSource } from "./helpers";

function SourceBadge({ source }: { source: string | null | undefined }) {
  const defined = isDefinedSource(source);
  const cls = defined ? SOURCE_BADGE_DEFINED_CLS : SOURCE_BADGE_REFERENCED_CLS;
  return <span className={cls}>{defined ? "Defined" : "Referenced"}</span>;
}

function GroupCell({ group }: { group: string | null | undefined }) {
  const label = group?.trim() || CONCEPT_GROUPS_COPY.ungroupedLabel;
  return (
    <span className="text-[12px] font-medium text-foreground break-words">
      {plainText(label)}
    </span>
  );
}

export const conceptTableColumns: Column<ConceptFinding>[] = [
  {
    key: "concept_id",
    header: "ID",
    align: "left",
    width: "108px",
    render: (row) => <ConceptIdChip id={row.concept_id} />,
  },
  {
    key: "name",
    header: "Concept",
    align: "left",
    minWidth: 180,
    render: (row) => (
      <span className="text-[13px] font-semibold text-foreground break-words">
        {plainText(row.name)}
      </span>
    ),
  },
  {
    key: "group",
    header: "Belongs to group",
    align: "left",
    minWidth: 160,
    render: (row) => <GroupCell group={row.group} />,
  },
  {
    key: "source",
    header: "Source",
    align: "left",
    width: "112px",
    render: (row) => <SourceBadge source={row.source} />,
  },
  {
    key: "section",
    header: "Section",
    align: "left",
    width: "88px",
    render: (row) => <SectionChip section={row.section} />,
  },
  {
    key: "page",
    header: "Page",
    align: "left",
    width: "72px",
    render: (row) => (
      <span className="text-[12px] text-muted-foreground tabular-nums">
        {row.page !== null && row.page !== undefined
          ? `p.${row.page}`
          : EMPTY_PLACEHOLDER}
      </span>
    ),
  },
  {
    key: "occurrences",
    header: "Mentions",
    align: "left",
    width: "96px",
    render: (row) => (
      <span className="text-[12px] font-semibold text-foreground tabular-nums">
        {(row.occurrences ?? 0).toLocaleString()}
      </span>
    ),
  },
  {
    key: "confidence",
    header: "Confidence",
    align: "left",
    width: "160px",
    render: (row) => <ConceptConfidenceBar conf={row.confidence} />,
  },
  {
    key: "description",
    header: "Description",
    align: "left",
    ellipsis: 2,
    tooltip: (row) => plainText(row.description) || undefined,
    render: (row) => (
      <span className="text-[12px] text-muted-foreground break-words">
        {plainText(row.description) || EMPTY_PLACEHOLDER}
      </span>
    ),
  },
];
